/**
 * Ordenação de tabela por clique no título da coluna.
 *
 * Um estado só por tabela, compartilhado entre a forma de tabela (desktop) e a
 * de cartões (celular): no celular não há cabeçalho para tocar, então o mesmo
 * estado ganha um seletor "Ordenar por".
 *
 * Regras que valem em todas as tabelas:
 *  · número começa do maior para o menor, texto de A a Z — a ordem mais útil
 *    para cada tipo no primeiro clique; o segundo clique inverte;
 *  · vazio vai sempre para o fim, nos dois sentidos — "sem consultor" no topo
 *    da lista não ajuda ninguém;
 *  · texto compara em português, sem diferenciar maiúscula e acento.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cx } from '../../lib/cx.ts';

type Valor = string | number | null | undefined;

export interface Ordem<C extends string> {
  coluna: C;
  desc: boolean;
}

export function useOrdenacao<T, C extends string>(
  itens: T[],
  colunas: Record<C, (item: T) => Valor>,
  inicial: Ordem<NoInfer<C>>,
) {
  const [ordem, setOrdem] = useState<Ordem<C>>(inicial);

  const ordenados = useMemo(() => {
    const ler = colunas[ordem.coluna];
    return [...itens].sort((a, b) => {
      const va = ler(a);
      const vb = ler(b);
      const vazioA = va === null || va === undefined || va === '';
      const vazioB = vb === null || vb === undefined || vb === '';
      if (vazioA || vazioB) return vazioA === vazioB ? 0 : vazioA ? 1 : -1;
      const r =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base', numeric: true });
      return ordem.desc ? -r : r;
    });
    // `colunas` é um objeto literal recriado a cada render; a ordem só muda
    // quando os itens ou a escolha mudam.
  }, [itens, ordem]);

  /** Clicar na mesma coluna inverte; trocar começa na ordem natural do tipo. */
  function alternar(coluna: C) {
    setOrdem((o) => {
      if (o.coluna === coluna) return { coluna, desc: !o.desc };
      const amostra = itens.map(colunas[coluna]).find((v) => v !== null && v !== undefined);
      return { coluna, desc: typeof amostra === 'number' };
    });
  }

  return { ordenados, ordem, alternar, definir: setOrdem };
}

/**
 * Título de coluna clicável, para cabeçalhos que não são `<table>` (grades).
 * A seta mostra a direção; sem ordenação na coluna, fica apagada.
 */
export function TituloOrdenavel<C extends string>({
  coluna,
  ordem,
  onOrdenar,
  children,
  direita,
  className,
}: {
  coluna: C;
  ordem: Ordem<C>;
  onOrdenar: (c: C) => void;
  children: ReactNode;
  direita?: boolean;
  className?: string;
}) {
  const ativo = ordem.coluna === coluna;
  const Seta = !ativo ? ArrowUpDown : ordem.desc ? ArrowDown : ArrowUp;
  return (
    <button
      type="button"
      onClick={() => onOrdenar(coluna)}
      aria-label={`Ordenar por ${typeof children === 'string' ? children : coluna}`}
      className={cx(
        'anel-foco flex min-w-0 items-center gap-1 whitespace-nowrap transition-colors',
        direita ? 'justify-end text-right' : 'text-left',
        ativo ? 'text-[var(--fg)]' : 'hover:text-[var(--fg)]',
        className,
      )}
    >
      <span className="truncate">{children}</span>
      <Seta size={12} aria-hidden className={cx('shrink-0', ativo ? 'opacity-100' : 'opacity-40')} />
    </button>
  );
}

/**
 * O mesmo, como célula de `<table>`. O `aria-sort` fica no `th`, que é onde o
 * leitor de tela procura; o botão ocupa a célula inteira para o alvo não ser
 * só a palavra.
 */
export function ThOrdenavel<C extends string>({
  className,
  ...props
}: {
  coluna: C;
  ordem: Ordem<C>;
  onOrdenar: (c: C) => void;
  children: ReactNode;
  direita?: boolean;
  className?: string;
}) {
  const ativo = props.ordem.coluna === props.coluna;
  return (
    <th
      aria-sort={ativo ? (props.ordem.desc ? 'descending' : 'ascending') : 'none'}
      className={cx('p-0 font-medium', className)}
    >
      <TituloOrdenavel {...props} className="w-full p-3" />
    </th>
  );
}

/**
 * "Ordenar por" para a forma de cartões. Select nativo: no celular abre o
 * seletor do sistema, que é maior e mais fácil de acertar que qualquer menu.
 */
export function SeletorOrdem<C extends string>({
  opcoes,
  ordem,
  onChange,
  className,
}: {
  opcoes: { coluna: C; rotulo: string }[];
  ordem: Ordem<C>;
  onChange: (o: Ordem<C>) => void;
  className?: string;
}) {
  return (
    <div className={cx('flex items-center gap-2 text-[13px]', className)}>
      <label className="flex min-w-0 flex-1 items-center gap-2 text-[var(--muted)]">
        <span className="shrink-0">Ordenar por</span>
        <select
          value={ordem.coluna}
          onChange={(e) => onChange({ ...ordem, coluna: e.target.value as C })}
          className="anel-foco h-10 min-w-0 flex-1 rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-2 text-[var(--fg)]"
        >
          {opcoes.map((o) => (
            <option key={o.coluna} value={o.coluna}>
              {o.rotulo}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={() => onChange({ ...ordem, desc: !ordem.desc })}
        aria-label={ordem.desc ? 'Decrescente — inverter' : 'Crescente — inverter'}
        className="anel-foco inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-[var(--fg)]"
      >
        {ordem.desc ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
        {ordem.desc ? 'Decresc.' : 'Cresc.'}
      </button>
    </div>
  );
}
